import NextLink from 'next/link';
import { Box, Link, ListItemText, Stack, SxProps } from '@mui/material';
import { NavItemProps } from '../type';
import Iconify from '../../Iconify';
import { ListItemStyle, ListItemTextStyle, ListItemIconStyle } from './style';
import { isExternalLink } from '..';
import BetaBadge from '~/components/BetaBadge';

export function NavItemRoot({ item, isCollapse, open = false, active, onOpen, sx }: NavItemProps) {
  const { title, path, icon, info, children } = item;

  const renderContent = (
    <>
      {isCollapse ? (
        <>
          <Stack direction="column" justifyContent="center" alignItems="center">
            {icon && <ListItemIconStyle sx={{ mr: 0 }}>{icon}</ListItemIconStyle>}
            {children && (
              <ArrowIcon open={false} sx={{ position: 'absolute', top: 7, right: 4, ml: 0 }} />
            )}
            <ListItemTextStyle disableTypography primary={title} isCollapse={isCollapse} />
          </Stack>
        </>
      ) : (
        <Stack direction="row" alignItems="center">
          {icon && <ListItemIconStyle>{icon}</ListItemIconStyle>}
          <ListItemTextStyle disableTypography primary={title} isCollapse={isCollapse} />
        </Stack>
      )}
      {!isCollapse && (
        <>
          {info && info}
          {children && <ArrowIcon open={open} />}
        </>
      )}
    </>
  );

  if (children) {
    return (
      <ListItemStyle
        onClick={onOpen}
        activeRoot={active}
        sx={{
          px: '12px',
          py: '4px',
          justifyContent: isCollapse ? 'center' : 'space-between',
          ...sx,
        }}
      >
        {renderContent}
      </ListItemStyle>
    );
  }

  return isExternalLink(path) ? (
    <ListItemStyle
      component={Link}
      href={path}
      target="_blank"
      rel="noopener"
      sx={{
        px: '12px',
        py: '4px',
        justifyContent: 'center',
        ...sx,
      }}
    >
      {renderContent}
    </ListItemStyle>
  ) : (
    <NextLink href={path} passHref>
      <ListItemStyle activeRoot={active} sx={{ mx: 2, ...sx }}>
        {renderContent}
      </ListItemStyle>
    </NextLink>
  );
}

type NavItemSubProps = Omit<NavItemProps, 'isCollapse'>;

export function NavItemSub({ item, open = false, active = false, onOpen }: NavItemSubProps) {
  const { title, path, info, children } = item;

  const renderContent = (
    <>
      {/* <DotIcon active={active} /> */}
      <ListItemText
        disableTypography
        primary={title}
        sx={{
          paddingLeft: '34px',
          color: active ? 'common.white' : 'primary.lighter',
        }}
      />
      {info && info}
      {children && <ArrowIcon open={open} />}
    </>
  );

  if (children) {
    return (
      <ListItemStyle onClick={onOpen} activeSub={active} subItem>
        {renderContent}
      </ListItemStyle>
    );
  }

  return isExternalLink(path) ? (
    <ListItemStyle component={Link} href={path} target="_blank" rel="noopener" subItem>
      {renderContent}
    </ListItemStyle>
  ) : (
    <NextLink href={path} passHref>
      <ListItemStyle activeSub={active} subItem>
        {renderContent}
      </ListItemStyle>
    </NextLink>
  );
}

type DotIconProps = {
  active: boolean;
};

export function DotIcon({ active }: DotIconProps) {
  return (
    <ListItemIconStyle>
      <Box
        component="span"
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          bgcolor: 'common.white',
          transition: (theme) =>
            theme.transitions.create('transform', {
              duration: theme.transitions.duration.shorter,
            }),
          ...(active && {
            transform: 'scale(2)',
          }),
        }}
      />
    </ListItemIconStyle>
  );
}

type ArrowIconProps = {
  open: boolean;
  sx?: SxProps;
};

export function ArrowIcon({ open, sx }: ArrowIconProps) {
  return (
    <Iconify
      icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
      sx={{ width: 16, height: 16, ml: 1, ...sx }}
    />
  );
}
